import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import LanguageSelector from '../../components/LanguageSelector';
import { useAuth } from '../../contexts/AuthContext';


type RegistrationType = 'org' | 'individual';

interface IndividualFormData {
  name: string;
  email: string;
  password: string;
  password_confirm: string;
}

interface OrgFormData extends IndividualFormData {
  company_name: string;
  company_size: string;
  position_in_company: string;
}

const COMPANY_SIZES = [
  { value: '', labelKey: 'selectSize' },
  { value: '1-10', labelKey: '1-10' },
  { value: '11-50', labelKey: '11-50' },
  { value: '51-200', labelKey: '51-200' },
  { value: '201-500', labelKey: '201-500' },
  { value: '500+', labelKey: '500+' },
];

const POSITION_KEYS = ['executive', 'admin', 'sysadmin', 'dpo', 'it_manager', 'compliance', 'security', 'other'] as const;

const inputClass =
  'w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50';
const labelClass = 'block text-sm font-medium text-gray-300 mb-2';
const iconWrapClass = 'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500';
const errorMsgClass = 'mt-1 text-xs text-red-400';

const Register: React.FC = () => {
  const { t } = useTranslation('register');
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [registrationType, setRegistrationType] = useState<RegistrationType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const individualForm = useForm<IndividualFormData>({
    defaultValues: { name: '', email: '', password: '', password_confirm: '' },
    mode: 'onTouched',
  });
  const orgForm = useForm<OrgFormData>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirm: '',
      company_name: '',
      company_size: '',
      position_in_company: '',
    },
    mode: 'onTouched',
  });

  const individualValues = individualForm.watch();
  const orgValues = orgForm.watch();

  // Re-run confirm password validation when password or confirm change so mismatch shows
  useEffect(() => {
    if (registrationType === 'individual') individualForm.trigger('password_confirm');
  }, [registrationType, individualValues.password, individualValues.password_confirm, individualForm]);
  useEffect(() => {
    if (registrationType === 'org') orgForm.trigger('password_confirm');
  }, [registrationType, orgValues.password, orgValues.password_confirm, orgForm]);

  const emailValidation = {
    required: t('form.email.required'),
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: t('form.email.invalid'),
    },
  };
  const nameValidation = {
    required: t('form.name.required'),
    minLength: { value: 1, message: t('form.name.minLength') },
    validate: (v: string) => (v.trim().length >= 1) || t('form.name.minLength'),
  };
  const passwordValidation = {
    required: t('form.password.required'),
    minLength: { value: 6, message: t('form.password.minLength') },
  };

  const isIndividualValid =
    individualValues.name.trim().length >= 1 &&
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(individualValues.email) &&
    individualValues.password.length >= 6 &&
    individualValues.password_confirm === individualValues.password;

  const isOrgValid =
    orgValues.name.trim().length >= 1 &&
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(orgValues.email) &&
    orgValues.company_name.trim().length >= 1 &&
    orgValues.company_size !== '' &&
    orgValues.position_in_company !== '' &&
    orgValues.password.length >= 6 &&
    orgValues.password_confirm === orgValues.password;

  const onIndividualSubmit = async (data: IndividualFormData) => {
    if (data.password !== data.password_confirm) {
      individualForm.setError('password_confirm', { message: t('form.passwordConfirm.match') });
      return;
    }
    const payload = {
      registration_type: 'individual' as const,
      name: data.name.trim(),
      email: data.email.trim().toLowerCase(),
      password: data.password,
    };
    try {
      setIsSubmitting(true);
      await registerUser(payload);
      toast.success(t('messages.checkEmail'));
      navigate(`/register/verify-email?email=${encodeURIComponent(payload.email)}`);
    } catch (e: any) {
      const message = e?.message || t('messages.failed');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onOrgSubmit = async (data: OrgFormData) => {
    if (data.password !== data.password_confirm) {
      orgForm.setError('password_confirm', { message: t('form.passwordConfirm.match') });
      return;
    }
    const payload = {
      registration_type: 'org' as const,
      name: data.name.trim(),
      email: data.email.trim().toLowerCase(),
      password: data.password,
      company_name: data.company_name.trim(),
      company_size: data.company_size || undefined,
      position_in_company: data.position_in_company || undefined,
    };
    try {
      setIsSubmitting(true);
      await registerUser(payload);
      toast.success(t('messages.checkEmail'));
      navigate(`/register/verify-email?email=${encodeURIComponent(payload.email)}`);
    } catch (e: any) {
      const message = e?.message || t('messages.failed');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col relative">
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pt-6 pb-12 sm:py-12">
        <div className={`w-full ${registrationType === 'org' ? 'max-w-2xl' : 'max-w-md'}`}>
          <div className="mb-8">
            <div className="flex items-start mb-6">
              <img src="/logo-gov.svg" alt="Governate Logo" className="h-16" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
            <p className="text-gray-400">{t('subtitle')}</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8 shadow-2xl">
            {registrationType === null ? (
              <>
                <p className="text-sm text-gray-300 mb-5">{t('step1.label')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setRegistrationType('org')}
                    className="flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-lg"
                  >
                    <div className="rounded-xl bg-white/10 p-3">
                      <BuildingOfficeIcon className="w-10 h-10" />
                    </div>
                    <span className="font-semibold text-base">{t('step1.org')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegistrationType('individual')}
                    className="flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-lg"
                  >
                    <div className="rounded-xl bg-white/10 p-3">
                      <UserIcon className="w-10 h-10" />
                    </div>
                    <span className="font-semibold text-base">{t('step1.individual')}</span>
                  </button>
                </div>
              </>
            ) : registrationType === 'individual' ? (
            <form onSubmit={individualForm.handleSubmit(onIndividualSubmit)} className="space-y-5" autoComplete="off">
              <button
                type="button"
                onClick={() => setRegistrationType(null)}
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                {t('back')}
              </button>
              <div>
                <label className={labelClass}>{t('form.name.label')}</label>
                <div className="relative">
                  <div className={iconWrapClass}>
                    <UserIcon className="h-5 w-5" />
                  </div>
                  <input
                    {...individualForm.register('name', nameValidation)}
                    type="text"
                    className={inputClass}
                    placeholder={t('form.name.placeholder')}
                  />
                </div>
                {individualForm.formState.errors.name && (
                  <p className={errorMsgClass}>{individualForm.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>{t('form.email.label')}</label>
                <div className="relative">
                  <div className={iconWrapClass}>
                    <EnvelopeIcon className="h-5 w-5" />
                  </div>
                  <input
                    {...individualForm.register('email', emailValidation)}
                    type="email"
                    className={inputClass}
                    placeholder={t('form.email.placeholder')}
                  />
                </div>
                {individualForm.formState.errors.email && (
                  <p className={errorMsgClass}>{individualForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>{t('form.password.label')}</label>
                <div className="relative">
                  <div className={iconWrapClass}>
                    <LockClosedIcon className="h-5 w-5" />
                  </div>
                  <input
                    {...individualForm.register('password', passwordValidation)}
                    type="password"
                    className={inputClass}
                    placeholder={t('form.password.placeholder')}
                  />
                </div>
                {individualForm.formState.errors.password && (
                  <p className={errorMsgClass}>{individualForm.formState.errors.password.message}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>{t('form.passwordConfirm.label')}</label>
                <div className="relative">
                  <div className={iconWrapClass}>
                    <LockClosedIcon className="h-5 w-5" />
                  </div>
                  <input
                    {...individualForm.register('password_confirm', {
                      required: t('form.passwordConfirm.required'),
                      validate: (v) => v === individualValues.password || t('form.passwordConfirm.match'),
                    })}
                    type="password"
                    className={inputClass}
                    placeholder={t('form.passwordConfirm.placeholder')}
                  />
                </div>
                {individualForm.formState.errors.password_confirm &&
                  (individualForm.formState.touchedFields?.password_confirm || individualForm.formState.isSubmitted) && (
                  <p className={errorMsgClass}>
                    {individualForm.formState.errors.password_confirm.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !isIndividualValid}
                className="w-full bg-transparent border-2 border-white text-white py-3 px-4 rounded-xl font-medium hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    {t('form.submit')}
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={orgForm.handleSubmit(onOrgSubmit)} className="space-y-5" autoComplete="off">
              <button
                type="button"
                onClick={() => setRegistrationType(null)}
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                {t('back')}
              </button>

              {/* Mobile: single column. Large: two columns - company left, user right */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {/* Left: Company */}
                <div className="space-y-5">
                  <p className="text-sm font-medium text-gray-400 lg:mb-4">Organization</p>
                  <div>
                    <label className={labelClass}>{t('form.company_name.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <BuildingOfficeIcon className="h-5 w-5" />
                      </div>
                      <input
                        {...orgForm.register('company_name', { required: t('form.company_name.required') })}
                        type="text"
                        className={inputClass}
                        placeholder={t('form.company_name.placeholder')}
                      />
                    </div>
                    {orgForm.formState.errors.company_name && (
                      <p className={errorMsgClass}>{orgForm.formState.errors.company_name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.company_size.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <UserGroupIcon className="h-5 w-5" />
                      </div>
                      <select
                        {...orgForm.register('company_size', { required: t('form.company_size.required') })}
                        className={inputClass + ' appearance-none'}
                      >
                        {COMPANY_SIZES.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value} className="bg-gray-900">
                            {opt.labelKey === 'selectSize'
                              ? t('form.company_size.placeholder')
                              : opt.labelKey === '1-10'
                                ? '1–10'
                                : opt.labelKey === '11-50'
                                  ? '11–50'
                                  : opt.labelKey === '51-200'
                                    ? '51–200'
                                    : opt.labelKey === '201-500'
                                      ? '201–500'
                                      : '500+'}
                          </option>
                        ))}
                      </select>
                    </div>
                    {orgForm.formState.errors.company_size && (
                      <p className={errorMsgClass}>{orgForm.formState.errors.company_size.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.position.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <BriefcaseIcon className="h-5 w-5" />
                      </div>
                      <select
                        {...orgForm.register('position_in_company', { required: t('form.position.required') })}
                        className={inputClass + ' appearance-none'}
                      >
                        <option value="" className="bg-gray-900">{t('form.position.placeholder')}</option>
                        {POSITION_KEYS.map((key) => (
                          <option key={key} value={key} className="bg-gray-900">
                            {t(`positions.${key}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {orgForm.formState.errors.position_in_company && (
                      <p className={errorMsgClass}>
                        {orgForm.formState.errors.position_in_company.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right: User / contact */}
                <div className="space-y-5">
                  <p className="text-sm font-medium text-gray-400 lg:mb-4">Your details</p>
                  <div>
                    <label className={labelClass}>{t('form.name.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <UserIcon className="h-5 w-5" />
                      </div>
                      <input
                        {...orgForm.register('name', nameValidation)}
                        type="text"
                        className={inputClass}
                        placeholder={t('form.name.placeholder')}
                      />
                    </div>
                    {orgForm.formState.errors.name && (
                      <p className={errorMsgClass}>{orgForm.formState.errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.email.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <EnvelopeIcon className="h-5 w-5" />
                      </div>
                      <input
                        {...orgForm.register('email', emailValidation)}
                        type="email"
                        className={inputClass}
                        placeholder={t('form.email.placeholder')}
                      />
                    </div>
                    {orgForm.formState.errors.email && (
                      <p className={errorMsgClass}>{orgForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.password.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <LockClosedIcon className="h-5 w-5" />
                      </div>
                      <input
                        {...orgForm.register('password', passwordValidation)}
                        type="password"
                        className={inputClass}
                        placeholder={t('form.password.placeholder')}
                      />
                    </div>
                    {orgForm.formState.errors.password && (
                      <p className={errorMsgClass}>{orgForm.formState.errors.password.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.passwordConfirm.label')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <LockClosedIcon className="h-5 w-5" />
                      </div>
                      <input
                        {...orgForm.register('password_confirm', {
                          required: t('form.passwordConfirm.required'),
                          validate: (v) => v === orgValues.password || t('form.passwordConfirm.match'),
                        })}
                        type="password"
                        className={inputClass}
                        placeholder={t('form.passwordConfirm.placeholder')}
                      />
                    </div>
                    {orgForm.formState.errors.password_confirm &&
                      (orgForm.formState.touchedFields?.password_confirm || orgForm.formState.isSubmitted) && (
                      <p className={errorMsgClass}>
                        {orgForm.formState.errors.password_confirm.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !isOrgValid}
                className="w-full bg-transparent border-2 border-white text-white py-3 px-4 rounded-xl font-medium hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group mt-4"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    {t('form.submit')}
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('footer')}{' '}
            <Link to="/login" className="text-white hover:underline font-medium">
              {t('loginLink')}
            </Link>
          </p>
        </div>
      </div>

      <footer className="py-4 text-center text-sm text-gray-500 shrink-0">
        {t('copyright')}
      </footer>
    </div>
  );
};

export default Register;
